import { z } from "zod";

// ============================================================================
// CALL LOGS SCHEMAS
// ============================================================================

/**
 * Call Log Entry Schema
 *
 * Represents a single call log entry from a phone call deployment session.
 * callMetadata contains call-specific details (from, duration, price, messages).
 */
export const CallLogEntrySchema = z.object({
  callMetadata: z
    .object({
      from: z.string().optional(),
      duration: z.union([z.string(), z.number()]).optional(),
      price: z.number().optional(),
      messages: z.array(z.unknown()).optional(),
      /** S3 URL to the full dual-channel call recording (WAV). Set after S3 migration. */
      recordingUrl: z.string().optional(),
      /** Whether the call was outbound (we called them) or inbound (they called us). */
      isOutbound: z.boolean().optional()
    })
    .passthrough()
    .nullable(),
  createdAt: z.string().datetime()
});

export type CallLogEntry = z.infer<typeof CallLogEntrySchema>;

/**
 * Get Call Logs Path Variables Schema
 */
export const GetCallLogsPathVarsSchema = z.object({
  id: z.string().uuid()
});

export type GetCallLogsPathVars = z.infer<typeof GetCallLogsPathVarsSchema>;

/**
 * Get Call Logs Response Schema
 *
 * Returns array of call log entries.
 */
export const GetCallLogsResponseSchema = z.array(CallLogEntrySchema);

export type GetCallLogsResponse = z.infer<typeof GetCallLogsResponseSchema>;

// ============================================================================
// CALL STATISTICS SCHEMAS
// ============================================================================

/**
 * Call Statistics Per Number Schema
 *
 * Statistics aggregated per phone number.
 */
export const CallStatisticsPerNumberSchema = z.object({
  number: z.string(),
  price: z.number(),
  duration: z.number(),
  messages: z.number(),
  updated: z.string().datetime()
});

export type CallStatisticsPerNumber = z.infer<typeof CallStatisticsPerNumberSchema>;

/**
 * Get Call Statistics Path Variables Schema
 */
export const GetCallStatisticsPathVarsSchema = z.object({
  id: z.string().uuid()
});

export type GetCallStatisticsPathVars = z.infer<typeof GetCallStatisticsPathVarsSchema>;

/**
 * Get Call Statistics Response Schema
 *
 * Returns aggregated call statistics for a phone call deployment.
 */
export const GetCallStatisticsResponseSchema = z.object({
  totalSpent: z.number(),
  totalNumberOfMessages: z.number(),
  totalTimeSpent: z.number(),
  data: z.array(CallStatisticsPerNumberSchema)
});

export type GetCallStatisticsResponse = z.infer<typeof GetCallStatisticsResponseSchema>;
